import { useEffect, useCallback } from 'react';
import { getSocket, disconnectSocket } from '../services/socket';

interface AutoCommenterAction {
  id: number;
  configId: number;
  postId: string;
  commentText: string;
  status: string;
  error?: string;
  createdAt: string;
  vkData?: {
    postText: string;
    postUrl: string;
    authorName: string;
    authorPhoto: string;
    date: number;
  } | null;
}

interface AutoCommenterStatus {
  configId: number;
  todayComments: number;
  isActive: boolean;
}

interface AutoCommenterError {
  configId: number;
  error: string;
}

export interface AutoCommenterProgress {
  configId: number;
  phase: 'searching' | 'processing' | 'waiting' | 'completed' | 'error';
  message: string;
  details?: {
    postsFound?: number;
    postsProcessed?: number;
    postsSkipped?: number;
    skippedReasons?: { reason: string; count: number }[];
    nextRunIn?: number;
    // Absolute deadline + initial duration for the local 1Hz countdown during
    // the `waiting` phase. See useWaitingCountdown.
    waitUntilMs?: number;
    waitMs?: number;
  };
}

interface UseAutoCommenterWebSocketProps {
  onAction?: (data: { configId: number; action: AutoCommenterAction; vkData?: any }) => void;
  onStatus?: (data: AutoCommenterStatus) => void;
  onError?: (data: AutoCommenterError) => void;
  onProgress?: (data: AutoCommenterProgress) => void;
}

export const useAutoCommenterWebSocket = ({
  onAction,
  onStatus,
  onError,
  onProgress,
}: UseAutoCommenterWebSocketProps) => {
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    if (onAction) socket.on('autoCommenter:action', onAction);
    if (onStatus) socket.on('autoCommenter:status', onStatus);
    if (onError) socket.on('autoCommenter:error', onError);
    if (onProgress) socket.on('autoCommenter:progress', onProgress);

    return () => {
      if (onAction) socket.off('autoCommenter:action', onAction);
      if (onStatus) socket.off('autoCommenter:status', onStatus);
      if (onError) socket.off('autoCommenter:error', onError);
      if (onProgress) socket.off('autoCommenter:progress', onProgress);
    };
  }, [onAction, onStatus, onError, onProgress]);

  const disconnect = useCallback(() => {
    disconnectSocket();
  }, []);

  return { disconnect };
};
